import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  catchError,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import {
  DownloadService,
  Go,
  ShowError,
  ShowInfo,
  WINDOW_REF
} from '@ui/legacy-lib';

import { DigitalContractFacade } from '../../core/services/digital-contract.facade';
import { SignContractStepsRoutes } from '../../components/digital-contract/sign-contract.childRoutes';
import { notificationConfig } from '../../config/notification.config';
import { DigitalContractState } from './digital-contract.reducers';
import {
  DIGITAL_CONTRACT_WIZARD_NEXT_STEP,
  DIGITAL_CONTRACT_WIZARD_PREVIOUS_STEP,
  DOWNLOAD_DIGITAL_CONTRACT,
  DigitalContractWizardNextStep,
  DownloadContract,
  DownloadContractFail,
  DownloadContractSuccess,
  GET_CURRENT_STATE,
  GET_SIGNING_URL,
  GO_TO_DOCUSIGN,
  GetCurrentState,
  GetCurrentStateFail,
  GetCurrentStateSuccess,
  GetSigningUrl,
  GetSigningUrlFail,
  GetSigningUrlSuccess,
  GoToDocuSign,
  LOAD_DIGITAL_CONTRACT,
  LoadDigitalContract,
  LoadDigitalContractFail,
  LoadDigitalContractSuccess,
  SEND_MESSAGE,
  SEND_TENANT_INFO,
  SIGN,
  SendMessage,
  SendMessageFail,
  SendMessageSuccess,
  SendTenantInfo,
  SendTenantInfoFail,
  SendTenantInfoSuccess,
  Sign,
  VERIFY_CODE,
  VerifyCode
} from './digital-contract.actions';
import { getContractWizardStepNumber } from './digital-contract.selectors';

@Injectable()
export class DigitalContractEffects {
  private actions$ = inject(Actions);
  sign$ = createEffect(() =>
    this.actions$.pipe(
      ofType<Sign>(SIGN),
      map(() => new Go({ path: ['digitalContract'] }))
    )
  );
  verifyCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType<VerifyCode>(VERIFY_CODE),
      map(
        () =>
          new Go({
            path: ['digitalContract', 'codeVerification']
          })
      )
    )
  );
  private facade = inject(DigitalContractFacade);
  loadContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDigitalContract>(LOAD_DIGITAL_CONTRACT),
      map(action => action.token),
      switchMap(token =>
        this.facade.loadDigitalContract(token).pipe(
          map(
            digitalContract => new LoadDigitalContractSuccess(digitalContract)
          ),
          catchError(error => [
            new ShowError(notificationConfig.digitalContract.load.error),
            new LoadDigitalContractFail(error)
          ])
        )
      )
    )
  );
  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendMessage>(SEND_MESSAGE),
      map(action => action.token),
      switchMap(token =>
        this.facade.sendMessage(token).pipe(
          mergeMap(() => [
            new SendMessageSuccess(),
            new ShowInfo(notificationConfig.digitalContract.sendMessage.success)
          ]),
          catchError(err => [
            new ShowError(notificationConfig.digitalContract.sendMessage.error),
            new SendMessageFail(err ? err.message : 'Unexpected error')
          ])
        )
      )
    )
  );
  sendTenantInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendTenantInfo>(SEND_TENANT_INFO),
      switchMap(({ info, signatureType }) =>
        this.facade.sendTenantInfo(info, signatureType).pipe(
          map(result => new SendTenantInfoSuccess(result)),
          catchError(err => [
            new ShowError(
              notificationConfig.digitalContract.sendTenantInfo.error
            ),
            new SendTenantInfoFail(err ? err.message : 'Unexpected error')
          ])
        )
      )
    )
  );
  getSigningUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetSigningUrl>(GET_SIGNING_URL),
      switchMap(({ token, redirectUrl }) =>
        this.facade.getSigningUrl(token, redirectUrl).pipe(
          map(docuSignResponse => new GetSigningUrlSuccess(docuSignResponse)),
          catchError(err => [
            new ShowError(
              notificationConfig.digitalContract.getSigningUrl.error
            ),
            new GetSigningUrlFail(err ? err.message : 'Unexpected error')
          ])
        )
      )
    )
  );
  currentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentState>(GET_CURRENT_STATE),
      switchMap(({ token }) =>
        this.facade.currentStatus(token).pipe(
          map(aesStatus => new GetCurrentStateSuccess(aesStatus)),
          catchError(err => [
            new ShowError(
              notificationConfig.digitalContract.getAesStatus.error
            ),
            new GetCurrentStateFail(err ? err.message : 'Unexpected error')
          ])
        )
      )
    )
  );
  private store = inject<Store<DigitalContractState>>(Store);
  changeStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DigitalContractWizardNextStep>(
        DIGITAL_CONTRACT_WIZARD_NEXT_STEP,
        DIGITAL_CONTRACT_WIZARD_PREVIOUS_STEP
      ),
      map(action => action.contractId || 'new'),
      withLatestFrom(this.store.select(getContractWizardStepNumber)),
      map(([_contractId, currentStepNumber]) => {
        return new Go({
          path: [
            'digitalContract',
            SignContractStepsRoutes[currentStepNumber - 1].path
          ]
        });
      })
    )
  );
  private downloadService = inject(DownloadService);
  downloadContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DownloadContract>(DOWNLOAD_DIGITAL_CONTRACT),
      tap(({ token, identifier }) =>
        this.downloadService.downloadDigitalContract(token, identifier)
      ),
      map(() => new DownloadContractSuccess()),
      catchError(error => [
        new DownloadContractFail(error),
        new ShowError(notificationConfig.digitalContract.download.error)
      ])
    )
  );
  private windowRef = inject(WINDOW_REF);
  goToDocuSign$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<GoToDocuSign>(GO_TO_DOCUSIGN),
        map(action => action.docuSignUrl),
        tap((docuSignUrl: string) => {
          this.windowRef.open(docuSignUrl, '_self');
        })
      ),
    { dispatch: false }
  );
}
